/**
 * Control Plane — LINE Login, Registration & OTP
 *
 * LINE OAuth flow, callback parsing, state management (CSRF),
 * LINE-based registration, SMS OTP, and phone re-verification.
 */

import {
  CONTROL_PLANE_URL,
  cpGuestApiCall,
  type RegisterResponse,
} from './controlPlaneClient'

// === LINE Login Types ===

export interface LineInitiateResponse {
  auth_url: string;
  state: string;
}

export interface LineCallbackParams {
  access_token: string;
  refresh_token: string;
  redirect: string;
  new_user: string;  // "1" or "0"
  tenant_id: string;
  tenant_slug?: string;
  user_id: string;
  server_url: string;
  company: string;
  is_provisioned: string;  // "1" or "0"
  storefront_url: string;
  state: string;  // CRITICAL: For CSRF validation
  expires_at?: string;  // Optional: JWT expiry from backend
  // Error case
  error?: string;
  message?: string;
}

// Storage key for OAuth state (CSRF protection)
const LINE_STATE_KEY = 'line_oauth_state';
const LINE_INTENT_KEY = 'line_oauth_intent';

export const storeLineState = (state: string, intent?: string): void => {
  // Use localStorage instead of sessionStorage — LINE's in-app browser
  // opens a new webview context for the OAuth callback, losing sessionStorage.
  localStorage.setItem(LINE_STATE_KEY, state);
  if (intent) {
    localStorage.setItem(LINE_INTENT_KEY, intent);
  } else {
    localStorage.removeItem(LINE_INTENT_KEY);
  }
};

export const getLineState = (): string | null => {
  return localStorage.getItem(LINE_STATE_KEY);
};

export const getLineIntent = (): string | null => {
  return localStorage.getItem(LINE_INTENT_KEY);
};

export const clearLineState = (): void => {
  localStorage.removeItem(LINE_STATE_KEY);
  localStorage.removeItem(LINE_INTENT_KEY);
};

// === LINE Login API Functions ===

/**
 * Initiate LINE OAuth login flow.
 * Returns auth_url to redirect user to LINE.
 */
export async function cpInitiateLineLogin(redirectAfter?: string, intent?: string): Promise<LineInitiateResponse> {
  const body: Record<string, string> = { redirect_after: redirectAfter || '/dashboard' };
  if (intent) body.intent = intent;
  return cpGuestApiCall<LineInitiateResponse>('control_plane.api.line.initiate_login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Parse LINE callback URL params into typed object.
 */
export function parseLineCallbackParams(search: string): LineCallbackParams | null {
  const params = new URLSearchParams(search);

  // Check for error first
  const error = params.get('error');
  if (error) {
    return {
      error,
      message: params.get('message') || 'Login failed',
      state: params.get('state') || '',  // State may be present on error too
    } as LineCallbackParams;
  }

  // Check for required success params
  const accessToken = params.get('access_token');
  const state = params.get('state');
  if (!accessToken || !state) {
    return null;  // Both access_token AND state are required
  }

  return {
    access_token: accessToken,
    refresh_token: params.get('refresh_token') || '',
    redirect: params.get('redirect') || '/dashboard',
    new_user: params.get('new_user') || '0',
    tenant_id: params.get('tenant_id') || '',
    user_id: params.get('user_id') || '',
    server_url: params.get('server_url') || '',
    company: params.get('company') || '',
    is_provisioned: params.get('is_provisioned') || '0',
    storefront_url: params.get('storefront_url') || '',
    state,
    expires_at: params.get('expires_at') || undefined,
  };
}

/**
 * Validate that callback state matches stored state (CSRF protection).
 */
export function validateLineState(callbackState: string): boolean {
  const storedState = getLineState();
  if (!storedState) {
    console.error('No stored state found for LINE OAuth');
    return false;
  }
  return callbackState === storedState;
}

// === LINE Registration Types and Functions ===

export interface LineRegisterPayload {
  line_user_id: string;
  first_name: string;
  last_name?: string;
  tenant_name: string;
  phone_number?: string;
  business_type?: string;
  app_interests?: string[];
  line_picture_url?: string;  // LINE profile picture URL
  intent?: string;  // LP intent (e.g. 'selling', 'chat', 'contacts')
  qualification_data?: string;  // Intent-specific qualification answers (JSON string)
  real_email?: string;  // Real email from desktop handoff (for Google Ads ECL)
}

/**
 * Register a new tenant from LINE OAuth profile.
 * Called after LINE user completes the registration wizard.
 */
export async function cpRegisterFromLine(payload: LineRegisterPayload): Promise<RegisterResponse> {
  const response = await fetch(`${CONTROL_PLANE_URL}/api/method/control_plane.api.line.register_from_line`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    let message = 'Registration failed';
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        let parsed = msgs[0];
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch { /* use as-is */ }
        }
        message = typeof parsed === 'object' && parsed?.message
          ? String(parsed.message)
          : String(parsed);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    }

    throw new Error(message);
  }

  const data = await response.json();
  return data.message;
}

// === SMS OTP Types and Functions ===

export interface SendOTPResponse {
  ref_code: string;
}

export interface VerifyOTPResponse {
  verified: boolean;
}

/**
 * Send OTP SMS to Thai mobile number.
 * Returns ref_code for user to identify the SMS.
 */
export async function cpSendOTP(mobile: string): Promise<SendOTPResponse> {
  return cpGuestApiCall<SendOTPResponse>('control_plane.api.otp.send_otp', {
    method: 'POST',
    body: JSON.stringify({ mobile }),
  });
}

/**
 * Verify OTP PIN code entered by user.
 */
export async function cpVerifyOTP(mobile: string, pin: string): Promise<VerifyOTPResponse> {
  return cpGuestApiCall<VerifyOTPResponse>('control_plane.api.otp.verify_otp', {
    method: 'POST',
    body: JSON.stringify({ mobile, pin }),
  });
}

/**
 * Complete phone re-verification for existing LINE user.
 * Uses opaque reverify_token (NOT raw line_user_id) for security.
 */
export async function cpCompleteReverify(
  reverifyToken: string,
  mobile: string
): Promise<{
  success: boolean;
  access_token: string;
  refresh_token: string;
  tenant_id: string;
  user_id: string;
  server_url: string;
  company: string;
  is_provisioned: boolean;
  storefront_url: string;
}> {
  return cpGuestApiCall('control_plane.api.otp.complete_reverify', {
    method: 'POST',
    body: JSON.stringify({ reverify_token: reverifyToken, mobile }),
  });
}
